import React from 'react';
import {
  tableHeaderRowClass, tableHeaderCellClass, tableRowClass,
} from 'features/activity/styles/dataTableStyles';

/**
 * Placeholder rows shaped like the table that is coming.
 *
 * The list pages each replaced their whole table with a centred spinner while
 * loading, so arriving data resized the container and pushed everything below
 * it — a spinner says "something is happening", which you already knew, and
 * then moves the page when it leaves.
 *
 * This renders the real header at the real column widths with `h-8` rows
 * underneath, the same geometry as the table it stands in for, so data landing
 * changes the content and nothing else. It reuses the shared table classes
 * rather than copying their values, so it cannot drift out of alignment when
 * those change.
 *
 * `columns` takes the labels when the caller knows them — real header text is
 * what makes the column widths right — or a count when it does not.
 */
/**
 * One shimmering placeholder bar.
 *
 * Exported so other skeletons reuse the treatment instead of copying it —
 * widths vary and the phase is offset per row, because a block of identical
 * synchronised bars reads as a progress indicator, which none of these are.
 */
export function SkeletonBar({ row = 0, col = 0, lead = false }) {
  return (
    <span
      className="block h-2 animate-pulse rounded bg-[color:var(--surface-hover)]"
      style={{
        width: lead ? '72%' : `${38 + ((row + col) % 4) * 12}%`,
        animationDelay: `${(row % 5) * 90}ms`,
      }}
    />
  );
}

export default function TableSkeleton({
  columns = 5,
  rows = 8,
  className = '',
}) {
  const labels = Array.isArray(columns) ? columns : null;
  const count = labels ? labels.length : Number(columns) || 5;

  return (
    <div
      className={`overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface ${className}`}
      // Announced as busy rather than silently changing under a screen reader.
      role="status"
      aria-busy="true"
      aria-label="Loading table"
    >
      <table className="w-full text-left" style={{ fontFamily: 'Inter, sans-serif' }}>
        <thead>
          <tr className={tableHeaderRowClass}>
            {Array.from({ length: count }).map((_, i) => (
              <th key={i} className={tableHeaderCellClass}>
                {labels ? labels[i] : <span className="inline-block h-2 w-16 rounded bg-[color:var(--surface-hover)]" />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r} className={`${tableRowClass} cursor-default hover:bg-transparent`}>
              {Array.from({ length: count }).map((_, c) => (
                <td key={c} className="px-3 align-middle">
                  {/* First column wider: it is almost always the identifier. */}
                  <SkeletonBar row={r} col={c} lead={c === 0} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
